import React, { useEffect, useState } from 'react';
import { ArrowLeft, ClipboardList, Lock } from 'lucide-react';
import api from '@/services/api';
import { MyAppraisal, AnonymousFeedback, AppraisalBody, DetailsCard } from './AppraisalForms';
import { MANAGERS, STATEMENTS, COMPANY_QUESTIONS } from './questions';

/**
 * End-of-year appraisal (Self Service): everyone's own appraisal and the
 * anonymous feedback. Team and Feedback are the HR views, used on
 * HR › Staff Appraisals (AppraisalResults.jsx).
 */
const CARD = 'rounded-xl border border-gray-200 bg-white';
const STATUS = {
  submitted: ['Submitted', 'bg-green-50 text-green-700'],
  started: ['Started', 'bg-amber-50 text-amber-700'],
  not_started: ['Not started', 'bg-gray-100 text-gray-600'],
};

export const Team = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    api.get('/appraisals/team').then((r) => setData(r.data?.data)).catch(() => setData({ staff: [] }));
  }, []);

  if (open) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={() => setOpen(null)} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" />Back to the list
        </button>
        <DetailsCard details={open.details} year={open.review_year} />
        <AppraisalBody answers={open.answers} readOnly />
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" /></div>;

  const staff = data.staff.filter((s) => `${s.name} ${s.department} ${s.job_title}`.toLowerCase().includes(q.trim().toLowerCase()));
  const done = data.staff.filter((s) => s.status === 'submitted').length;
  const view = async (id) => {
    const r = await api.get(`/appraisals/${id}`);
    setOpen(r.data?.data);
  };

  return (
    <section className={`${CARD} overflow-hidden`}>
      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600"><span className="font-semibold text-gray-900">{done}</span> of {data.staff.length} submitted</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff" className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm sm:w-64" />
      </div>
      {staff.map((s) => (
        <div key={s.employee_id} className="flex items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{s.name}</p>
            <p className="truncate text-xs text-gray-500">{[s.job_title, s.department].filter(Boolean).join(', ')}</p>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[s.status][1]}`}>{STATUS[s.status][0]}</span>
          {s.appraisal_id ? (
            <button type="button" onClick={() => view(s.appraisal_id)} className="text-sm font-medium text-primary-700 hover:underline">Read</button>
          ) : <span className="w-[34px]" />}
        </div>
      ))}
      {!staff.length && <p className="px-4 py-12 text-center text-sm text-gray-500">No staff to show.</p>}
    </section>
  );
};

const Comments = ({ title, items }) =>
  items?.length ? (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{title} ({items.length})</p>
      <ul className="space-y-1.5">{items.map((t, i) => <li key={i} className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{t}</li>)}</ul>
    </div>
  ) : null;

export const Feedback = ({ year }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/appraisals/feedback', { params: { year } }).then((r) => setData(r.data?.data)).catch(() => setData({ responses: 0 }));
  }, [year]);

  if (!data) return <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" /></div>;
  if (!data.responses) return <p className={`${CARD} px-4 py-12 text-center text-sm text-gray-500`}>No anonymous feedback yet for {year}.</p>;
  const few = data.responses < 3;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
        <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
        <p><span className="font-semibold text-gray-900">{data.responses}</span> {data.responses === 1 ? 'person has' : 'people have'} sent anonymous feedback. Comments are shown in a random order.{few && ' With so few replies, take care: it may be easy to guess who wrote what.'}</p>
      </div>

      {MANAGERS.map(([m, title]) => {
        const r = data.managers[m];
        return (
          <section key={m} className={`${CARD} p-5`}>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <div className="mt-3 divide-y divide-gray-100">
              {STATEMENTS.map(([s, statement]) => {
                const st = r.statements[s];
                return (
                  <div key={s} className="py-2.5">
                    <div className="flex items-center gap-3">
                      <p className="min-w-0 flex-1 text-sm text-gray-700">{statement}</p>
                      <div className="hidden h-2 w-32 overflow-hidden rounded-full bg-gray-100 sm:block">
                        <div className="h-full rounded-full bg-primary-600" style={{ width: `${((st.average || 0) / 5) * 100}%` }} />
                      </div>
                      <p className="w-14 text-right text-sm font-semibold tabular-nums text-gray-900">{st.average ?? '-'}<span className="text-xs font-normal text-gray-400">/5</span></p>
                    </div>
                    {st.comments.length > 0 && <ul className="mt-1.5 space-y-1">{st.comments.map((c, i) => <li key={i} className="text-xs text-gray-500">"{c}"</li>)}</ul>}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Comments title="Does well" items={r.well} />
              <Comments title="Should change" items={r.change} />
            </div>
          </section>
        );
      })}

      <section className={`${CARD} space-y-4 p-5`}>
        <h3 className="text-base font-semibold text-gray-900">The company overall</h3>
        {COMPANY_QUESTIONS.map(([k, label]) => <Comments key={k} title={label} items={data.company[k]} />)}
      </section>

      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-gray-100 p-5">
          <h3 className="text-base font-semibold text-gray-900">Best colleagues</h3>
          <p className="mt-1 text-sm text-gray-500">3 points for a first place, 2 for second and 1 for third.</p>
        </div>
        {data.colleagues.map((c, i) => (
          <details key={c.employee_id} className="border-b border-gray-100 last:border-0">
            <summary className="flex cursor-pointer items-center gap-3 px-5 py-3 hover:bg-gray-50">
              <span className="w-6 text-sm font-semibold tabular-nums text-gray-400">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{c.name}</span>
              <span className="text-xs text-gray-500">{c.mentions} {c.mentions === 1 ? 'mention' : 'mentions'}, {c.first} first</span>
              <span className="w-16 text-right text-sm font-semibold tabular-nums text-gray-900">{c.points} pts</span>
            </summary>
            <ul className="space-y-1.5 px-5 pb-4 pl-14">{c.reasons.map((t, j) => <li key={j} className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{t}</li>)}</ul>
          </details>
        ))}
      </section>
    </div>
  );
};

const AppraisalPage = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState('mine');

  const load = () =>
    api.get('/appraisals/me').then((r) => setData(r.data?.data)).catch(() => setFailed(true));
  useEffect(() => { load(); }, []);

  if (failed) return <p className="py-16 text-center text-sm text-red-700">Couldn't load the appraisal. Refresh the page to try again.</p>;
  if (!data) return <div className="flex justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" /></div>;

  const tabs = [
    ['mine', 'My appraisal', ClipboardList],
    ['anonymous', 'Anonymous feedback', Lock],
  ];

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 pb-16">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Staff appraisal {data.year}</h1>
        <p className="mt-1 text-sm text-gray-500">End-of-year review, completed by every Puffin employee.</p>
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${tab === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
            <Icon className="h-4 w-4" />{label}
            {k === 'mine' && data.appraisal?.status !== 'submitted' && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
            {k === 'anonymous' && !data.anonymousDone && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
          </button>
        ))}
      </div>
      {tab === 'mine' && <MyAppraisal key={data.appraisal?.status || 'new'} data={data} onSaved={load} />}
      {tab === 'anonymous' && <AnonymousFeedback done={data.anonymousDone} onSent={load} />}
    </div>
  );
};

export default AppraisalPage;
